import { useCallback, useState } from 'react';

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

export interface CacheOptions {
  ttl?: number; // Time to live in milliseconds (default: 5 minutes)
  key?: string; // Cache key override
}

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

export class ApiCache {
  private cache = new Map<string, CacheEntry<any>>();
  private pendingRequests = new Map<string, Promise<any>>();

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) {
      return null;
    }

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  set<T>(key: string, data: T, ttl: number = DEFAULT_TTL): void {
    const now = Date.now();
    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + ttl
    });
  }

  delete(key: string): void {
    this.cache.delete(key);
    this.pendingRequests.delete(key);
  }

  clear(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  // Deduplicate identical requests in flight
  async getOrFetch<T>(
    key: string,
    fetcher: () => Promise<T>,
    options: CacheOptions = {}
  ): Promise<T> {
    const cacheKey = options.key || key;
    const ttl = options.ttl || DEFAULT_TTL;

    // Check cache first
    const cached = this.get<T>(cacheKey);
    if (cached) {
      return cached;
    }

    // Check if request is already in flight
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    // Start new request
    const request = fetcher();
    this.pendingRequests.set(cacheKey, request);

    try {
      const result = await request;
      this.set(cacheKey, result, ttl);
      return result;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  // Invalidate cache entries by pattern
  invalidatePattern(pattern: string): void {
    const regex = new RegExp(pattern);
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    }
    for (const key of this.pendingRequests.keys()) {
      if (regex.test(key)) {
        this.pendingRequests.delete(key);
      }
    }
  }

  // Get cache statistics
  getStats() {
    return {
      size: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      entries: Array.from(this.cache.entries()).map(([key, entry]) => ({
        key,
        timestamp: entry.timestamp,
        expiresAt: entry.expiresAt,
        isExpired: Date.now() > entry.expiresAt
      }))
    };
  }
}

// Global cache instance
const globalCache = new ApiCache();

export const useApiCache = () => {
  const [cacheStats, setCacheStats] = useState(() => globalCache.getStats());

  const updateStats = useCallback(() => {
    setCacheStats(globalCache.getStats());
  }, []);

  const getOrFetch = useCallback(async <T>(
    key: string,
    fetcher: () => Promise<T>,
    options: CacheOptions = {}
  ): Promise<T> => {
    const result = await globalCache.getOrFetch(key, fetcher, options);
    updateStats();
    return result;
  }, [updateStats]);

  const invalidate = useCallback((key: string) => {
    globalCache.delete(key);
    updateStats();
  }, [updateStats]);

  const invalidatePattern = useCallback((pattern: string) => {
    globalCache.invalidatePattern(pattern);
    updateStats();
  }, [updateStats]);

  const clear = useCallback(() => {
    globalCache.clear();
    updateStats();
  }, [updateStats]);

  const get = useCallback(<T>(key: string): T | null => {
    return globalCache.get<T>(key);
  }, []);

  return {
    getOrFetch,
    invalidate,
    invalidatePattern,
    clear,
    get,
    cacheStats
  };
};

// Cache key generators for common API endpoints
export const CacheKeys = {
  sites: () => 'sites',
  accessibleSites: () => 'sites:accessible',
  siteById: (id: string) => `sites:${id}`,
  users: (roles?: string[], createdBy?: string | null) => {
    const rolesKey = roles?.join(',') || 'all';
    const createdByKey = createdBy || 'any';
    return `users:${rolesKey}:${createdByKey}`;
  },
  managedClients: (params: any) => `users:managed:${JSON.stringify(params)}`,
  drafts: () => 'drafts',
  draftById: (id: string) => `drafts:${id}`
};

export { globalCache };